import React, { useState, useRef, useEffect } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { usePrescriptionColumns } from './hooks/usePrescriptionColumns';
import { usePrescriptionFormFields } from './hooks/usePrescriptionFormFields';
import { DATE_FILTER_OPTIONS } from './constants';
import {
  PrinterOutlined,
  CopyOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { PrescriptionPrintComponent } from './components';
import { message } from 'antd';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';

interface PrescriptionsTabProps {
  patientId: string | number;
  patient?: any; // Add patient data prop
}

interface PrintRef {
  print: () => void;
}

export const PrescriptionsTab: React.FC<PrescriptionsTabProps> = ({
  patientId,
  patient,
}) => {
  const [selectedRecordForPrint, setSelectedRecordForPrint] =
    useState<any>(null);
  const [copyPrescriptionData, setCopyPrescriptionData] = useState<any>(null);
  const [autoOpenCreate, setAutoOpenCreate] = useState(false);
  const [copyId, setCopyId] = useState<number>(0);
  const [formInstanceKey, setFormInstanceKey] = useState<string>('');

  const printRef = useRef<PrintRef>(null);
  const navigate = useNavigate();

  // Reset copy state when patient changes
  useEffect(() => {
    setCopyPrescriptionData(null);
    setAutoOpenCreate(false);
    setCopyId(0);
    setFormInstanceKey('');
  }, [patientId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setCopyPrescriptionData(null);
      setAutoOpenCreate(false);
      setCopyId(0);
      setFormInstanceKey('');
    };
  }, []);

  // Additional cleanup effect to ensure copy data is cleared when needed
  useEffect(() => {
    if (copyPrescriptionData && !autoOpenCreate) {
      // If we have copy data but the form is not open, clear it after a delay
      const timer = setTimeout(() => {
        setCopyPrescriptionData(null);
        setCopyId(0);
        setFormInstanceKey('');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [copyPrescriptionData, autoOpenCreate]);

  const onPrintClick = (record: any) => {
    setSelectedRecordForPrint(record);
    setTimeout(() => {
      printRef.current?.print();
    }, 100);
  };

  const onCopyClick = (record: any) => {
    if (!record) {
      console.warn('No record provided for copying');
      return;
    }

    // Prevent copying if there's already copy data in progress
    if (copyPrescriptionData) {
      message.warning(
        'Please complete or cancel the current copy operation first.'
      );
      return;
    }

    try {
      // Generate a new copy ID, timestamp, and unique form instance key
      const newCopyId = copyId + 1;
      const timestamp = Date.now();
      const uniqueFormKey = `form_${timestamp}_${newCopyId}_${Math.random().toString(36).substr(2, 9)}`;

      setCopyId(newCopyId);
      setFormInstanceKey(uniqueFormKey);

      // Prepare the data to copy, excluding id and timestamps
      const copyData = {
        copyId: newCopyId, // Add unique identifier
        timestamp: timestamp, // Add timestamp for additional uniqueness
        formInstanceKey: uniqueFormKey, // Add unique form instance key
        patient_id: patientId,
        prescribed_at: (() => {
          const now = dayjs();
          return now.second(0).millisecond(0);
        })(), // Set to current date/time when copying
        prescribed_by: record.prescribed_by?.id || record.prescribed_by,
        notes: record.notes || '',
        lab_report: record.lab_report || '',
        today_procedure: record.today_procedure || '',
        prescription_medicines: Array.isArray(record.prescription_medicines)
          ? record.prescription_medicines.map(
              (medicine: any, index: number) => ({
                medicine_id: medicine.medicine?.id || null,
                name: medicine.name || '',
                dosage: medicine.dosage || '',
                when_take: medicine.when_take || '',
                where_take: medicine.where_take || '',
                qty: medicine.qty || 1,
                instruction: medicine.instruction || '',
                id: `copied_medicine_${index}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                _action: 'create' as const, // Set as copied row to show green background
                _originalData: { ...medicine }, // Store original data for comparison
              })
            )
          : [],
      };

      // Set the copy data and trigger auto-open
      setCopyPrescriptionData(copyData);
      setAutoOpenCreate(true);

      message.success('Prescription copied successfully!');
    } catch (error) {
      console.error('Error copying prescription data:', error);
      message.error('Failed to copy prescription. Please try again.');
    }
  };

  const onCreateSaleClick = (record: any) => {
    if (!record) {
      message.error('No prescription selected for creating sale');
      return;
    }

    try {
      // Helper function to calculate base price (amount before GST)
      const calculateBasePrice = (
        mrp: number,
        quantity: number,
        packQuantity: number,
        gstRate: number
      ): number => {
        if (
          !mrp ||
          !quantity ||
          !packQuantity ||
          mrp <= 0 ||
          quantity <= 0 ||
          packQuantity <= 0
        ) {
          return 0;
        }
        const totalMRP = (mrp * quantity) / packQuantity;
        const gstMultiplier = 1 + (gstRate || 0) / 100;
        const result = gstMultiplier > 0 ? totalMRP / gstMultiplier : 0;
        return Math.round(result * 100) / 100;
      };

      // Helper function to calculate GST amount
      const calculateGSTAmount = (
        basePrice: number,
        gstRate: number
      ): number => {
        if (!basePrice || basePrice <= 0 || !gstRate || gstRate <= 0) {
          return 0;
        }
        const result = (basePrice * gstRate) / 100;
        return Math.round(result * 100) / 100;
      };

      // Prepare sale data from prescription
      const validMedicines = Array.isArray(record.prescription_medicines)
        ? record.prescription_medicines.filter(
            (medicine: any) => medicine.medicine?.id
          )
        : [];

      let totalSubtotal = 0;
      let totalTax = 0;
      let totalNetAmount = 0;

      const saleItems = validMedicines.map((medicine: any) => {
        const mrp = parseFloat(medicine.medicine?.mrp || '0');
        const quantity = parseInt(medicine.qty || '1');
        const packQuantity = parseFloat(
          medicine.medicine?.pack_quantity || '1'
        );
        const gstRate = parseFloat(medicine.medicine?.gst_rate || '0');

        // Calculate base price (amount before GST)
        const amount = calculateBasePrice(mrp, quantity, packQuantity, gstRate);

        // Calculate GST amount
        const gstAmount = calculateGSTAmount(amount, gstRate);

        // Calculate net amount (base price + GST)
        const netAmount = amount + gstAmount;

        // Add to totals
        totalSubtotal += amount;
        totalTax += gstAmount;
        totalNetAmount += netAmount;

        return {
          id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          // Set the correct medicine_id and inventory_id
          medicine_id: medicine.medicine.id,
          inventory_id: medicine.inventory?.id || medicine.medicine.id,
          // Create the medicine object that the sales form expects
          medicine: {
            id: medicine.medicine.id,
            name: medicine.name || '',
            hsn: medicine.medicine?.hsn || '',
            pack_size: medicine.medicine?.pack_size || '',
            pack_quantity: packQuantity,
            mrp: mrp,
            gst_rate: gstRate,
            // Add inventory object that the sales form expects
            inventory: {
              id: medicine.inventory?.id || medicine.medicine.id,
              current_stock: medicine.inventory?.current_stock || 1,
              purchase_price: 0,
              location: 'main-storage',
            },
          },
          medicine_name: medicine.name || '',
          mrp: mrp,
          quantity: quantity,
          discount: 0,
          gst_rate: gstRate,
          amount: amount, // Base price before GST
          discount_amount: 0,
          discounted_base_price: amount, // Same as amount when no discount
          gst_amount: gstAmount,
          net_amount: netAmount,
          _action: 'create' as const,
          fromPrescription: true,
        };
      });

      const saleData = {
        patient_id: patientId,
        customer_name: patient
          ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
          : '',
        customer_phone: patient?.mobile_no?.substring(2) || '',
        date: new Date().toISOString(),
        notes: '',
        sale_items: saleItems,
        total_rate: Math.round(totalSubtotal * 100) / 100,
        subtotal: Math.round(totalSubtotal * 100) / 100,
        discount: 0,
        tax_amount: Math.round(totalTax * 100) / 100,
        total_amount: Math.round(totalNetAmount * 100) / 100,
        overall_discount_rate: 0,
        overall_discount_amount: 0,
        bill_amount: Math.round(totalNetAmount * 100) / 100,
        due_amount: Math.round(totalNetAmount * 100) / 100,
        clinic_due_amount: 0,
        payment_status: 'UNPAID',
        fromPrescription: true,
      };

      // Store sale data in localStorage to be picked up by the sales page
      localStorage.setItem('prescriptionToSaleData', JSON.stringify(saleData));

      // Navigate to sales page
      navigate('/medical?tab=sales');

      message.success(
        'Redirecting to sales page to create sale from prescription'
      );
    } catch (error) {
      console.error('Error creating sale from prescription:', error);
      message.error(
        'Failed to create sale from prescription. Please try again.'
      );
    }
  };

  const handleCreateSuccess = () => {
    // Reset the copy data and auto-open state after successful creation
    setCopyPrescriptionData(null);
    setAutoOpenCreate(false);
    // Also reset the copy ID and form instance key to ensure complete cleanup
    setCopyId(0);
    setFormInstanceKey('');
  };

  const handleCreateDrawerClose = () => {
    // Reset the copy data and auto-open state when drawer is closed
    setCopyPrescriptionData(null);
    setAutoOpenCreate(false);
    // Also reset the copy ID and form instance key to ensure complete cleanup
    setCopyId(0);
    setFormInstanceKey('');
  };

  const columns = usePrescriptionColumns();
  const formFields = usePrescriptionFormFields(patientId.toString());

  // Helper function to ensure proper row states for medicine data
  const ensureProperRowStates = (medicines: any[]) => {
    if (!Array.isArray(medicines)) return [];

    return medicines.map((medicine, index) => ({
      ...medicine,
      id: medicine.id || `medicine_${index}_${Date.now()}`,
      _action: medicine._action || '', // Clean state for existing medicines
      _originalData: medicine._originalData || { ...medicine },
    }));
  };

  // Create unique form fields for each copy operation to ensure complete isolation
  const uniqueFormFields = React.useMemo(() => {
    if (!copyPrescriptionData || !formInstanceKey) return formFields;

    // Create completely fresh form fields with unique identifiers
    return formFields.map(field => ({
      ...field,
      // Add unique identifier to prevent any form state mixing
      _uniqueId: `${field.name}_${formInstanceKey}`,
      // Ensure the field is completely fresh
      _copyOperationId: copyPrescriptionData.copyId,
    }));
  }, [formFields, copyPrescriptionData, formInstanceKey]);

  // Custom actions for the table
  const customActions = [
    {
      key: 'copy',
      icon: <CopyOutlined />,
      label: '',
      onClick: onCopyClick,
      type: 'default' as const,
      tooltip: 'Copy Prescription',
    },
    {
      key: 'print',
      icon: <PrinterOutlined />,
      label: '',
      onClick: onPrintClick,
      type: 'default' as const,
      tooltip: 'Print Prescription',
    },
    {
      key: 'createSale',
      icon: <ShoppingCartOutlined />,
      label: '',
      onClick: onCreateSaleClick,
      type: 'default' as const,
      tooltip: 'Create Sale from Prescription',
    },
  ];

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SmartDataTable
        key={formInstanceKey || `prescriptions-table-${patientId}`}
        api={{
          resource: `prescriptions`,
          defaultParams: {
            filters: [
              {
                field: 'patient_id',
                operator: 'eq',
                value: patientId.toString(),
              },
            ],
          },
        }}
        title="Prescriptions"
        columns={columns}
        search={{
          enabled: true,
          fields: ['medicine_name'],
          placeholder: 'Search by medicine name...',
          debounceMs: 500,
        }}
        createForm={{
          fields: uniqueFormFields as any,
          title: `Create Prescription`,
          submitText: 'Create Prescription',
          initialValues: copyPrescriptionData
            ? {
                ...copyPrescriptionData,
                _copyTimestamp: copyPrescriptionData.timestamp, // Force form refresh
                _copyId: copyPrescriptionData.copyId, // Additional uniqueness
                _formInstanceKey: copyPrescriptionData.formInstanceKey, // Form instance uniqueness
              }
            : {
                patient_id: patientId,
              },
        }}
        editForm={{
          fields: uniqueFormFields as any,
          title: 'Edit Prescription',
          submitText: 'Save Changes',
        }}
        formDisplayMode="drawer"
        drawerWidth={1300}
        modalWidth={1000}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={false}
        pageSize={10}
        pageOptions={[10, 20, 50, 100]}
        showSizeChanger={true}
        showQuickJumper={false}
        showExport={false}
        showColumnVisibility={true}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 375px)',
          },
        }}
        rowKey="id"
        customActions={customActions}
        autoOpenCreate={autoOpenCreate}
        onCreateSuccess={handleCreateSuccess}
        onCreateDrawerClose={handleCreateDrawerClose}
      />

      {/* Print Component */}
      {selectedRecordForPrint && (
        <PrescriptionPrintComponent
          ref={printRef}
          record={selectedRecordForPrint}
          patient={patient}
        />
      )}
    </div>
  );
};
